import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// ── Mortgage Calculator ──────────────────────────────────────────────────────
function MortgageCalc() {
  const [form, setForm] = useState({ price: 600000, down: 120000, rate: 5.5, amort: 25 });
  const [result, setResult] = useState(null);

  const calculate = () => {
    const loanAmount = form.price - form.down;
    const downPct = (form.down / form.price) * 100;
    let cmhc = 0;
    if (downPct < 5) { setResult({ error: "Minimum down payment is 5%." }); return; }
    if (downPct < 10) cmhc = loanAmount * 0.04;
    else if (downPct < 20) cmhc = loanAmount * 0.031;
    const totalLoan = loanAmount + cmhc;
    const monthlyRate = form.rate / 100 / 12;
    const n = form.amort * 12;
    const monthly = totalLoan * (monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
    // Stress test (higher of rate+2% or 5.25%)
    const stressRate = Math.max(form.rate + 2, 5.25) / 100 / 12;
    const stressMonthly = totalLoan * (stressRate * Math.pow(1 + stressRate, n)) / (Math.pow(1 + stressRate, n) - 1);
    setResult({ monthly, stressMonthly, totalLoan, cmhc, downPct });
  };

  const fmt = (v) => new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(v);

  return (
    <div className="space-y-6" data-testid="mortgage-calc">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          { label: "Home price ($)", key: "price", min: 100000, max: 5000000, step: 10000 },
          { label: "Down payment ($)", key: "down", min: 5000, max: 2000000, step: 5000 },
          { label: "Interest rate (%)", key: "rate", min: 0.5, max: 15, step: 0.05 },
          { label: "Amortization (years)", key: "amort", min: 5, max: 30, step: 5 },
        ].map(({ label, key, min, max, step }) => (
          <div key={key} className="space-y-1.5">
            <label className="text-sm font-semibold text-[#1C1C1C]">{label}</label>
            <input
              type="number"
              min={min} max={max} step={step}
              value={form[key]}
              onChange={(e) => setForm({ ...form, [key]: parseFloat(e.target.value) })}
              data-testid={`mortgage-${key}`}
              className="w-full border border-[#E7E5E4] rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[#1B4332] bg-white text-[#1C1C1C]"
            />
          </div>
        ))}
      </div>
      <button
        onClick={calculate}
        data-testid="mortgage-calculate"
        className="bg-[#1B4332] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#2D6A4F] transition-colors"
      >
        Calculate
      </button>
      {result && !result.error && (
        <div className="bg-[#F2EFE9] border border-[#E7E5E4] rounded-xl p-6 space-y-4" data-testid="mortgage-result">
          <h3 className="font-bold text-[#1C1C1C] text-lg">Your results</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border border-[#E7E5E4]">
              <p className="text-xs text-[#57534E] mb-1">Monthly payment</p>
              <p className="font-bold text-2xl text-[#1B4332]">{fmt(result.monthly)}</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-[#E7E5E4]">
              <p className="text-xs text-[#57534E] mb-1">Stress test payment</p>
              <p className="font-bold text-2xl text-[#D4A373]">{fmt(result.stressMonthly)}</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-[#E7E5E4]">
              <p className="text-xs text-[#57534E] mb-1">Total loan (incl. CMHC)</p>
              <p className="font-bold text-xl text-[#1C1C1C]">{fmt(result.totalLoan)}</p>
            </div>
            {result.cmhc > 0 && (
              <div className="bg-white rounded-lg p-4 border border-[#E7E5E4]">
                <p className="text-xs text-[#57534E] mb-1">CMHC insurance</p>
                <p className="font-bold text-xl text-[#1C1C1C]">{fmt(result.cmhc)}</p>
              </div>
            )}
          </div>
          <p className="text-xs text-[#A8A29E]">
            Down: {result.downPct.toFixed(1)}% · Stress test uses {Math.max(form.rate + 2, 5.25).toFixed(2)}% qualifying rate (OSFI rules)
          </p>
        </div>
      )}
      {result?.error && <p className="text-red-600 text-sm">{result.error}</p>}
    </div>
  );
}

// ── Budget Calculator ────────────────────────────────────────────────────────
function BudgetCalc() {
  const [income, setIncome] = useState(5000);
  const [items, setItems] = useState([
    { label: "Rent", amount: 2000 },
    { label: "Groceries", amount: 500 },
    { label: "Transit", amount: 156 },
    { label: "Phone", amount: 55 },
    { label: "Utilities", amount: 100 },
    { label: "Entertainment", amount: 150 },
    { label: "Savings", amount: 500 },
  ]);

  const total = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const remaining = income - total;
  const fmt = (v) => new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(v);

  return (
    <div className="space-y-6" data-testid="budget-calc">
      <div className="space-y-1.5">
        <label className="text-sm font-semibold text-[#1C1C1C]">Monthly take-home income ($)</label>
        <input
          type="number"
          value={income}
          onChange={(e) => setIncome(parseFloat(e.target.value))}
          data-testid="budget-income"
          className="w-full border border-[#E7E5E4] rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[#1B4332] bg-white text-[#1C1C1C]"
        />
      </div>
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="flex gap-3 items-center">
            <input
              type="text"
              value={item.label}
              onChange={(e) => { const n = [...items]; n[idx].label = e.target.value; setItems(n); }}
              className="flex-1 border border-[#E7E5E4] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1B4332] bg-white text-[#1C1C1C]"
            />
            <input
              type="number"
              value={item.amount}
              onChange={(e) => { const n = [...items]; n[idx].amount = e.target.value; setItems(n); }}
              className="w-28 border border-[#E7E5E4] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1B4332] bg-white text-[#1C1C1C]"
            />
            <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600 text-sm font-bold px-1">×</button>
          </div>
        ))}
        <button
          onClick={() => setItems([...items, { label: "New item", amount: 0 }])}
          className="text-[#1B4332] text-sm font-semibold hover:underline"
        >
          + Add item
        </button>
      </div>
      <div className={`rounded-xl p-6 border ${remaining >= 0 ? "bg-[#F2EFE9] border-[#E7E5E4]" : "bg-red-50 border-red-200"}`}>
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm text-[#57534E]">Total expenses</p>
            <p className="font-bold text-2xl text-[#1C1C1C]">{fmt(total)}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-[#57534E]">Remaining</p>
            <p className={`font-bold text-2xl ${remaining >= 0 ? "text-[#1B4332]" : "text-red-600"}`}>{fmt(remaining)}</p>
          </div>
        </div>
        {remaining < 0 && <p className="text-red-600 text-sm mt-3">You're spending {fmt(Math.abs(remaining))} more than you earn each month.</p>}
      </div>
    </div>
  );
}

// ── Cost of Living Estimator ──────────────────────────────────────────────────
function CostCalc() {
  const [city, setCity] = useState("Toronto");
  const [size, setSize] = useState("single");

  const data = {
    Toronto: {
      single: { rent: 2400, groceries: 450, transit: 156, utilities: 80, phone: 55, total: 3141 },
      family: { rent: 3200, groceries: 900, transit: 312, utilities: 150, phone: 110, total: 4672 },
    },
    Vancouver: {
      single: { rent: 2650, groceries: 470, transit: 109, utilities: 120, phone: 55, total: 3404 },
      family: { rent: 3500, groceries: 940, transit: 218, utilities: 200, phone: 110, total: 4968 },
    },
    Calgary: {
      single: { rent: 1850, groceries: 420, transit: 115, utilities: 140, phone: 55, total: 2580 },
      family: { rent: 2400, groceries: 840, transit: 230, utilities: 220, phone: 110, total: 3800 },
    },
    Montreal: {
      single: { rent: 1500, groceries: 400, transit: 97, utilities: 80, phone: 50, total: 2127 },
      family: { rent: 2100, groceries: 800, transit: 194, utilities: 130, phone: 100, total: 3324 },
    },
  };

  const c = data[city][size];
  const fmt = (v) => `$${v.toLocaleString()}`;

  return (
    <div className="space-y-6" data-testid="cost-calc">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-[#1C1C1C]">City</label>
          <select
            value={city}
            onChange={(e) => setCity(e.target.value)}
            data-testid="cost-city"
            className="w-full border border-[#E7E5E4] rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[#1B4332] bg-white text-[#1C1C1C]"
          >
            {Object.keys(data).map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-[#1C1C1C]">Household</label>
          <select
            value={size}
            onChange={(e) => setSize(e.target.value)}
            data-testid="cost-size"
            className="w-full border border-[#E7E5E4] rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[#1B4332] bg-white text-[#1C1C1C]"
          >
            <option value="single">Single person</option>
            <option value="family">Family of 4</option>
          </select>
        </div>
      </div>
      <div className="bg-[#F2EFE9] rounded-xl p-6 space-y-3">
        {[
          { label: "Rent (1BR / 3BR)", value: c.rent },
          { label: "Groceries", value: c.groceries },
          { label: "Transit pass", value: c.transit },
          { label: "Utilities", value: c.utilities },
          { label: "Phone", value: c.phone },
        ].map(({ label, value }) => (
          <div key={label} className="flex justify-between items-center py-2 border-b border-[#E7E5E4] last:border-0">
            <span className="text-sm text-[#57534E]">{label}</span>
            <span className="font-semibold text-[#1C1C1C]">{fmt(value)}/mo</span>
          </div>
        ))}
        <div className="flex justify-between items-center pt-2">
          <span className="font-bold text-[#1C1C1C]">Estimated total</span>
          <span className="font-bold text-xl text-[#1B4332]">{fmt(c.total)}/mo</span>
        </div>
      </div>
      <p className="text-xs text-[#A8A29E]">Based on typical mid-range costs for {city}, {new Date().getFullYear()}. Actual costs vary significantly by neighbourhood.</p>
    </div>
  );
}

// ── Credit Builder ───────────────────────────────────────────────────────────
function CreditCalc() {
  const [startScore, setStartScore] = useState(0);
  const [actions, setActions] = useState({
    securedCard: false, authorizedUser: false, creditBuilderLoan: false, onTimePayments: 0,
  });

  const projectedMonths = () => {
    let base = 18;
    if (actions.securedCard) base -= 4;
    if (actions.authorizedUser) base -= 3;
    if (actions.creditBuilderLoan) base -= 2;
    if (actions.onTimePayments >= 12) base -= 3;
    else if (actions.onTimePayments >= 6) base -= 1;
    return Math.max(base, 6);
  };

  const months = projectedMonths();
  const targetScore = 700;

  return (
    <div className="space-y-6" data-testid="credit-calc">
      <div className="space-y-4">
        <p className="text-sm font-semibold text-[#1C1C1C]">Which credit-building actions are you taking?</p>
        {[
          { key: "securedCard", label: "Opened a secured credit card" },
          { key: "authorizedUser", label: "Added as authorized user on someone's card" },
          { key: "creditBuilderLoan", label: "Opened a credit-builder loan" },
        ].map(({ key, label }) => (
          <label key={key} className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={actions[key]}
              onChange={(e) => setActions({ ...actions, [key]: e.target.checked })}
              className="w-4 h-4 accent-[#1B4332]"
            />
            <span className="text-sm text-[#1C1C1C]">{label}</span>
          </label>
        ))}
        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-[#1C1C1C]">On-time payments made so far</label>
          <input
            type="number"
            min={0} max={60}
            value={actions.onTimePayments}
            onChange={(e) => setActions({ ...actions, onTimePayments: parseInt(e.target.value) || 0 })}
            className="w-full border border-[#E7E5E4] rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[#1B4332] bg-white text-[#1C1C1C]"
          />
        </div>
      </div>
      <div className="bg-[#F2EFE9] rounded-xl p-6 text-center space-y-2">
        <p className="text-[#57534E] text-sm">Estimated time to reach 700+</p>
        <p className="font-bold text-5xl text-[#1B4332]">{months}</p>
        <p className="font-semibold text-[#1C1C1C]">months</p>
        <p className="text-xs text-[#A8A29E] mt-3">
          This is an estimate based on typical credit bureau behaviour in Canada. Individual results vary.
        </p>
      </div>
    </div>
  );
}

// ── Main Tools Page ──────────────────────────────────────────────────────────
const TOOLS = [
  { slug: "mortgage", label: "Mortgage Affordability", num: "01", desc: "What a Canadian lender will actually approve — stress test included.", Component: MortgageCalc },
  { slug: "budget", label: "Monthly Budget", num: "02", desc: "A flexible budget that fits real Canadian living costs.", Component: BudgetCalc },
  { slug: "cost-of-living", label: "Cost-of-Living Estimator", num: "03", desc: "Compare monthly costs across Toronto, Vancouver, Calgary and more.", Component: CostCalc },
  { slug: "credit-builder", label: "Credit-Builder Timeline", num: "04", desc: "See how long it actually takes to reach a 700+ score from zero.", Component: CreditCalc },
];

export default function Tools() {
  const { tool } = useParams();
  const [active, setActive] = useState(tool || "mortgage");
  const current = TOOLS.find((t) => t.slug === active) || TOOLS[0];
  const { Component } = current;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <section className="py-16 px-6 border-b border-[#E7E5E4] bg-white">
        <div className="max-w-screen-xl mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373] mb-3">Tools</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#1C1C1C] tracking-tight">
            Run the numbers on your own situation.
          </h1>
          <p className="text-[#57534E] mt-4 text-lg max-w-2xl">
            Four straightforward calculators — built around how Canadian mortgages, budgets, and credit actually work.
          </p>
        </div>
      </section>

      <div className="max-w-screen-xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <aside className="space-y-2">
          {TOOLS.map((t) => (
            <button
              key={t.slug}
              onClick={() => setActive(t.slug)}
              data-testid={`tool-tab-${t.slug}`}
              className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                active === t.slug
                  ? "bg-[#1B4332] text-white border-[#1B4332]"
                  : "bg-white border-[#E7E5E4] text-[#57534E] hover:border-[#1B4332] hover:text-[#1B4332]"
              }`}
            >
              <p className="text-xs font-bold tracking-widest text-current opacity-60 mb-0.5">Tool {t.num}</p>
              <p className="font-semibold text-sm">{t.label}</p>
            </button>
          ))}
        </aside>

        {/* Calculator */}
        <div className="lg:col-span-3">
          <div className="bg-white border border-[#E7E5E4] rounded-xl p-8">
            <h2 className="font-bold text-xl text-[#1C1C1C] mb-2">{current.label}</h2>
            <p className="text-[#57534E] text-sm mb-8">{current.desc}</p>
            <Component />
          </div>
        </div>
      </div>
    </div>
  );
}
